/**
 * `PlanTemplateFullResponse` → `PlanDraft`. **The highest-consequence module in this app.**
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/responseToDraft.ts`, and built first,
 * in a read-only pull request, for the reason the execution ADR §3 gives: a bug here silently
 * deletes every manual override the trainer ever set, and it does so on the NEXT save rather than
 * on this load, where nobody would connect the two.
 *
 * ── The hazard, stated once ──────────────────────────────────────────────────────────────────
 * `GET /plan-template/{id}/full` returns **materialised** `templateSets` — every cycle of every
 * exercise, expanded by the engine — and never the authored `sets` plus `setOverrides` that
 * produced them. Reconstructing the authored tree therefore means two selections, and both are
 * strict:
 *
 *   · **The authored base is the rows whose `cycleIndex` equals the exercise's own
 *     `activationCycle`** — not cycle 1, and not the phase's first cycle. An exercise added
 *     mid-plan has rows only from its activation onward, and the backend computes its baseline
 *     from there.
 *   · **Every row tagged `isManuallyOverridden` becomes an override**, at the absolute coordinate
 *     the wire gave it — with one exclusion. A row on the exercise's OWN base cycle may legally
 *     carry the flag, and `authoredSets` has already read it as the authored prescription, so it
 *     is skipped rather than read a second time as an override of itself. See `readOverrides`,
 *     which states the consequence, and `anOverrideSittingOnTheBaseCycleIsNotReadTwiceAsBothBaseAndOverride`,
 *     which pins it. Mobile assumes the base cycle is always materialised un-overridden; this app
 *     stops making that assumption about the server.
 *
 * Because the save is whole-tree replace and the request is the sole source of overrides
 * (document-model ADR §2 — an omitted cell is CLEARED), anything this function fails to read back
 * is data the trainer loses.
 *
 * ── Two collapses that mirror the forward mapper ──────────────────────────────────────────────
 *   · **One builder day = the day's first session.** Its `estimatedDurationMinutes` becomes the
 *     day's duration, its `defaultRestTimeSeconds` the day's rest seed, its exercises the day's
 *     exercises. A day carrying MORE than one session is refused rather than truncated — see
 *     `readPlanDocument`.
 *   · **One authored base cycle per exercise.** The full per-cycle grid is NOT captured: the
 *     backend regenerates it from the template on every save, so the draft only ever needs the
 *     authored base plus the manual overrides.
 *
 * ── What the port changed, and why ───────────────────────────────────────────────────────────
 *
 * 1. **`tempId` minting is gone**, with the field (see `planDraft.types.ts`).
 * 2. **`dropIds` is gone.** It served mobile's `?sourceTemplateId=` prefill-as-new flow, which
 *    this app has no route for. Adding a parameter with one possible value would be a seam for a
 *    variation that does not exist — `CLAUDE.md`'s "one adapter is a hypothetical seam".
 * 3. **The category is carried verbatim** rather than normalised with a `'strength'` fallback.
 *    The reason is on `PlanDraft.category`: a whole-tree save would persist the fallback.
 * 4. **`showsRestColumn` is derived per DAY, at the end of `dayToDraft`.** The per-exercise mapper
 *    writes `false` — which is what mobile's own does before its day-level pass — and the day pass
 *    then runs `deriveRestColumns` (D3 ∘ D12: shown when a set's rest differs from the day's, vetoed
 *    on a superset member whose rest can never fire). PR-A1 stopped at the `false`, deliberately,
 *    because nothing observed the flag until the set table existed; PR-B2C closes it, and closing it
 *    is not cosmetic — a plan whose rests were authored on the phone opened here with the column
 *    HIDDEN, so the trainer could neither see nor correct a prescription they had written.
 * 5. **The session's own name is captured** as `DayDraft.sessionName`. Mobile keeps only the DAY
 *    name; the v3 canon titles the workout card with the SESSION's name ("Push A") while the day
 *    header carries the weekday. Both are on the draft, and the projection decides.
 * 6. **`PhaseDraft.startCycle` is captured** from `phase.startCycle`. Mobile recomputes it by
 *    walking phases; keeping the server's own value means the load-time coordinates and the wire's
 *    agree exactly, which is what the override coordinates are judged against.
 * 7. **`prescribedTempo` and `prescribedSpeed` are read.** Mobile reads neither and therefore wipes
 *    both on its next save. See change 5 in `planDraft.types.ts` — this is the one place the port
 *    diverges deliberately, and it diverges toward not destroying data.
 * 8. **An override row sitting on the exercise's own BASE cycle is skipped**, so no cell is read as
 *    both the authored prescription and an override of itself. See `readOverrides`.
 * 9. **`typeKey` is parsed against a closed set** rather than lower-cased blindly, so an
 *    unrecognised wire value resolves to `null` instead of to a key nothing can render.
 *
 * ── The freeform contract, 15 August 2026 ────────────────────────────────────────────────────
 * The backend gave days and exercises a per-cycle lifespan and replaced the progression rule with a
 * map this app cannot yet read. Everything it added is READ here, whether or not a surface renders
 * it, because of the invariant this slice's AGENTS.md states first: the save is whole-tree replace,
 * so an unread field is a DELETED field. Three of them are worse than deleted — an omitted
 * `retirementCycle`, `excludedCycles` or day `activationCycle` does not narrow a window, it WIDENS
 * it, minting the client workouts the trainer had removed.
 *
 * · **`progressionRules` is READ** — `rulesFromWire` turns the wire's map into this app's own, once,
 *   here. It was carried opaquely for a month while the projection engine was frozen against the
 *   deleted document shape; the port ended that. The mapping is total, so a rule on a column no
 *   surface here can show still survives a load and a whole-tree save.
 * · **`liveClosedCycles` (per day) and `liveCyclesClosedToNewDays` (plan-wide) are captured** so a
 *   gesture can grey itself instead of provoking a refusal. Only the FULL read populates either.
 * · **`cycles` becomes `serverCycles`** on both the day and the exercise: server-resolved, captured,
 *   never emitted. Renamed at this seam because `PhaseDraft.cycles` is a COUNT, and because
 *   `saveConflict` excludes non-authored state by key name.
 * · **`coverImageKey` became `coverId` plus a read-only `coverImageUrl`.**
 */

import type {
  TrainWithMeModulesPlansDtosResponsesDayTemplateResponse,
  TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponse,
  TrainWithMeModulesPlansDtosResponsesPhaseTemplateResponse,
  TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse,
  TrainWithMeModulesPlansDtosResponsesTemplateSetResponse,
} from '~/shared/api/generated/model';

import { measurementFieldsFromWire } from '~/shared/measurement';

import { dayRestSeconds, deriveRestColumns } from './dayRest';
import { AUTHORED_ORIGIN } from './draftFactory';
import {
  executionModeFromApi,
  exerciseTypeKeyFromApi,
  phaseTypeFromApi,
  schedulingModeFromApi,
} from './planEnums';
import type {
  DayDraft,
  ExerciseDraft,
  PhaseDraft,
  PlanDraft,
  ProgressionOverride,
  ServerDayWindow,
  ServerPhaseStructure,
  ServerPlanStructure,
  SetDraft,
} from './planDraft.types';
import { rulesFromWire } from './progressionWire';
import { executionsFromWire } from './slotExecutions';
import { newSetLocalId, slotTokenOrMint } from './slotToken';

type FullResponse = TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse;
type PhaseResponse = TrainWithMeModulesPlansDtosResponsesPhaseTemplateResponse;
type DayResponse = TrainWithMeModulesPlansDtosResponsesDayTemplateResponse;
type ExerciseResponse = TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponse;
type SetResponse = TrainWithMeModulesPlansDtosResponsesTemplateSetResponse;

/**
 * The plan's name, so the screen can head a refusal with the plan it is about.
 *
 * A refusal still knows the name — the response arrived intact and only its SHAPE or its SYSTEM is
 * unusable — and the heading is what tells two open tabs apart and what the shell announces on
 * arrival. Without it the trainer is told "#44 cannot be opened", which names a number they have
 * never seen in the interface. `null` when the response carried no name at all.
 */
interface RefusedPlanName {
  readonly planName: string | null;
}

/**
 * Why the whole plan was refused. A discriminated union, which is what let the second case be an
 * added member rather than a boolean nobody can name.
 */
export type PlanDocumentRefusal =
  | (RefusedPlanName & {
      readonly reason: 'multiSessionDay';
      /** How many days carry more than one session. Shown to the trainer — a count is checkable. */
      readonly dayCount: number;
    })
  | (RefusedPlanName & {
      /**
       * The plan runs on a scheduling system this build cannot draw. The only canvas the builder
       * has is the weekday grid, and a save resubmits the WHOLE tree with `DateBased` on it — so
       * opening a self-paced plan here would not merely mis-draw it, it would convert it.
       */
      readonly reason: 'unsupportedSchedulingMode';
    });

/**
 * The result of reading one plan. Either an editable draft, or a refusal with a reason a screen
 * can word.
 */
export type PlanDocumentRead =
  | { readonly outcome: 'draft'; readonly draft: PlanDraft }
  | { readonly outcome: 'refused'; readonly refusal: PlanDocumentRefusal };

/**
 * A 1-based cycle coordinate, or `null` when the wire carries no usable one.
 *
 * `?? null` alone would treat a `0` — or a negative — as a REAL coordinate, and a base cycle of 0
 * matches no set row: the exercise then loads with zero sets and the next save wipes its
 * prescription. The backend floors these, so this is the safety belt rather than the gate.
 */
function cycleCoordinate(raw: number | null | undefined): number | null {
  return raw != null && raw > 0 ? raw : null;
}

function setToDraft(set: SetResponse, index: number): SetDraft {
  return {
    // Minted on the way in, exactly as a day's or an exercise's token is when the stored one is
    // blank: nothing on the wire carries this, so a load is one of the doors it comes through.
    localId: newSetLocalId(),
    setIndex: set.setIndex ?? index + 1,
    setType: set.setType ?? null,
    prescribedWeight: set.prescribedWeight ?? null,
    prescribedReps: set.prescribedReps ?? null,
    prescribedRpe: set.prescribedRpe ?? null,
    prescribedRir: set.prescribedRir ?? null,
    prescribedRestTime: set.prescribedRestTime ?? null,
    prescribedDurationSeconds: set.prescribedDurationSeconds ?? null,
    prescribedDistanceMeters: set.prescribedDistanceMeters ?? null,
    prescribedRounds: set.prescribedRounds ?? null,
    prescribedPercent: set.prescribedPercent ?? null,
    // Read back so the next whole-tree save re-emits them. Mobile reads neither and therefore wipes
    // both on every save it makes — see change 5 in `planDraft.types.ts`. The save is the sole
    // source of truth for a set's values, so a field this mapper skips is a field the trainer loses
    // the moment anything at all is saved.
    prescribedTempo: set.prescribedTempo ?? null,
    prescribedSpeed: set.prescribedSpeed ?? null,
    // Read with no editor above it, for the same reason: a ceiling authored on the phone is deleted
    // by this app's next save unless it comes back through here.
    prescribedCeiling: set.prescribedCeiling ?? null,
    // Always null on a load: `Set` has no note column, so there is nothing on the wire for this to
    // come from. It is the mocked G4 field — see `SetDraft.note`.
    note: null,
  };
}

/**
 * The exercise's AUTHORED base sets — the rows of its BASE cycle — ordered by `setIndex`.
 *
 * The base cycle is the exercise's own `activationCycle`: the backend guarantees an exercise has
 * set rows ONLY in `[activationCycle .. end of phase]`, and that an exercise present from the
 * phase's start carries `activationCycle === phase.startCycle`.
 *
 * **The filter is STRICT, and that is the whole point.** No rows in the base cycle means the
 * exercise has no authored base there. Inventing one from another cycle's rows would silently pump
 * the set count on the next save — mobile's documented 4 → 8 → 16 defect — and would mis-state the
 * trainer's prescription in the process.
 *
 * `baseCycle` is null only for a response carrying neither `activationCycle` nor the phase's
 * `startCycle`. That path keeps mobile's behaviour verbatim — cycle 1, falling back to every row
 * when nothing is tagged — because an untagged payload would otherwise load with zero sets.
 */
function authoredSets(exercise: ExerciseResponse, baseCycle: number | null): SetDraft[] {
  const all = exercise.templateSets ?? [];
  const ordered = (rows: readonly SetResponse[]): SetDraft[] =>
    [...rows].sort((a, b) => (a.setIndex ?? 0) - (b.setIndex ?? 0)).map(setToDraft);
  if (baseCycle != null) return ordered(all.filter((set) => set.cycleIndex === baseCycle));
  const cycleOne = all.filter((set) => set.cycleIndex === 1);
  return ordered(cycleOne.length > 0 ? cycleOne : all);
}

/**
 * The manually-overridden cells: every template set tagged `isManuallyOverridden`.
 *
 * `cycleIndex` and `setIndex` are absolute and positional straight off the wire, so they round-trip
 * back out unchanged. A row missing either coordinate is dropped — it cannot address a cell, and
 * emitting it at `(0, 0)` on the next save would be a 400 at best.
 *
 * ── EVERY prescribed column is read, and the ladder filter is NOT applied here ────────────────
 * Weight, percent, RPE and RIR came across until 15 August 2026 and the other seven did not, which
 * live QA proved is a WIPE rather than an omission: the materialiser overlays only the columns a
 * request supplies, so a reps pin the phone wrote reverted to the base value on this app's next
 * save while the cell stayed flagged as overridden. All eleven are read now. See
 * `ProgressionOverride`, which carries the repro.
 *
 * It is the WRITE seam that narrows them: `overrideWireInputs` ships a ladder member's percent
 * alone — because the backend rejects a weight or an effort target on a row whose load is derived
 * in session from the top set's actual — drops the row entirely when it has no percent to send, and
 * gates the measurement columns on the exercise's own contract. `progressionRuleWire.ts` records
 * all of it so it cannot be missed.
 *
 * Reading wide and writing narrow is the right way round. A read that pre-filtered would discard a
 * value the backend is currently storing, and the trainer would watch a cell empty itself on load.
 *
 * ── The base cycle is skipped ────────────────────────────────────────────────────────────────
 * The wire lets a row at the exercise's own base cycle carry the flag, and `authoredSets` has
 * already read that row as the AUTHORED prescription. Reading it a second time as an override would
 * make one cell both the base and an override of itself: the next save would emit the same value
 * twice, and any later edit to the base would be shadowed by the stale override still pinned over
 * it. Mobile does not guard this because its own comment assumes the base cycle is always
 * materialised un-overridden — an assumption about the server that costs nothing to stop making.
 *
 * ── QA note: the base-cycle override is PROMOTED, and that is visible ────────────────────────
 * Skipping the row does not discard its value. `authoredSets` has already read that same row as the
 * exercise's authored prescription, so the pinned number survives — as the BASE rather than as an
 * override. The observable consequence, and the thing to check when a plan carrying one is opened
 * and saved: the cell keeps its value and its whole column re-projects from it, but it stops being
 * flagged `isManuallyOverridden` on the round trip, so the matrix draws it as authored rather than
 * as pinned. That is the honest outcome — one cell cannot be both — and it is a state change the
 * trainer can see, so it is recorded here rather than left to be discovered.
 */
function readOverrides(exercise: ExerciseResponse, baseCycle: number | null): ProgressionOverride[] {
  return (exercise.templateSets ?? [])
    .filter((set) => set.isManuallyOverridden === true)
    .filter((set) => baseCycle == null || set.cycleIndex !== baseCycle)
    .map((set) => ({
      setIndex: set.setIndex ?? 0,
      cycleIndex: set.cycleIndex ?? 0,
      prescribedWeight: set.prescribedWeight ?? null,
      prescribedPercent: set.prescribedPercent ?? null,
      prescribedRpe: set.prescribedRpe ?? null,
      prescribedRir: set.prescribedRir ?? null,
      prescribedReps: set.prescribedReps ?? null,
      prescribedRestTime: set.prescribedRestTime ?? null,
      prescribedDurationSeconds: set.prescribedDurationSeconds ?? null,
      prescribedDistanceMeters: set.prescribedDistanceMeters ?? null,
      prescribedRounds: set.prescribedRounds ?? null,
      prescribedSpeed: set.prescribedSpeed ?? null,
      prescribedTempo: set.prescribedTempo ?? null,
      isManuallyOverridden: true as const,
    }))
    .filter((override) => override.setIndex > 0 && override.cycleIndex > 0);
}

/**
 * Map one exercise. `phaseStartCycle` is the owning phase's absolute first cycle — the fallback
 * base cycle for an exercise carrying no `activationCycle` of its own.
 *
 * Exported, with `dayToDraft` and `phaseToDraft`, for `saveShapeToDraft.ts` alone: a proposal's
 * items arrive in the save shape and are read into the draft through these same three readers, so
 * the tree has one reading of an item however it arrived. Not on the slice's barrel.
 */
export function exerciseToDraft(exercise: ExerciseResponse, phaseStartCycle: number | null): ExerciseDraft {
  const activationCycle = cycleCoordinate(exercise.activationCycle);
  // One base cycle, resolved once and used by BOTH selections. Reading the authored rows off one
  // cycle and the overrides off another would let a single row be claimed twice.
  const baseCycle = activationCycle ?? phaseStartCycle;
  const sets = authoredSets(exercise, baseCycle);
  const storedRules = rulesFromWire(exercise.progressionRules);
  return {
    // Echoed back VERBATIM so the next save is recognised as the SAME exercise slot and keeps its
    // activation cycle and its progression chain. Re-minting here would make every save look like
    // a delete-and-recreate — which is what `slotTokenOrMint` does ONLY for a blank token, where
    // the row predates the migration that added the column and cannot be resent as it stands.
    slotToken: slotTokenOrMint(exercise.slotToken),
    exerciseId: exercise.exerciseId ?? 0,
    name: exercise.exerciseName ?? '',
    activationCycle,
    typeKey: exerciseTypeKeyFromApi(exercise.exerciseType),
    role: exercise.role ?? 'Main',
    measurementFields: measurementFieldsFromWire(exercise.measurementFields),
    // The rule map, READ — and beside it the entries this build has no reading for, held verbatim so
    // a newer backend's rule survives this client's next whole-tree save. Built once, here: identity
    // is load-bearing (Immer patches, store selectors), so nothing may rebuild either per read.
    progressionRules: storedRules.rules,
    unreadableProgressionRules: storedRules.unreadable,
    realMaxMultiplier: exercise.realMaxMultiplier ?? null,
    // Verbatim, never normalised: a whole-tree save persists whatever this holds, so mapping an
    // unrecognised window onto a default would change the trainer's rule as a side effect of opening
    // the plan. Same reasoning as `PlanDraft.category`.
    realMaxSource: exercise.realMaxSource ?? null,
    // The exercise's own cycle lifespan. Read before anything authors it, because an omitted end is
    // a WIDENED window on the way out — see the field's own note.
    retirementCycle: cycleCoordinate(exercise.retirementCycle),
    excludedCycles: exercise.excludedCycles ?? [],
    // Resolved by the server from this window and the day's; captured, never emitted.
    serverCycles: exercise.cycles ?? null,
    progressionOverrides: readOverrides(exercise, baseCycle),
    // `AbsoluteWeight` is the backend's own default. A null must NOT become some other value: the
    // enum is `AbsoluteWeight | PercentOfOneRm`, and a later load-then-save round trip would 400.
    prescriptionMode: exercise.prescriptionMode ?? 'AbsoluteWeight',
    // TRIMMED, and the empty string collapses to `null` — mobile's own truthy gate
    // (`exercise.group || null`). A bracket id is a letter the trainer picked; `""` and `"   "` are
    // the absence of one, and the block walk brackets CONTIGUOUS exercises sharing a NON-NULL
    // group. Carried through as `""` they would compare equal to each other and fuse two unrelated
    // adjacent exercises into a superset the trainer never authored — a rail drawn across a pairing
    // that does not exist, and one that then round-trips back to the backend on the next save.
    group: exercise.group?.trim() || null,
    // The coaching tip, read back so the next whole-tree save re-emits it instead of deleting it.
    // TRIMMED at this seam rather than at each reader: the backend stores `Notes` verbatim and this
    // app is not the only writer, so a `"   "` off the wire would otherwise light every "has a tip"
    // affordance over a cue that renders as nothing. One representation of "no tip", decided where
    // the data enters.
    notes: exercise.notes?.trim() || null,
    // Nothing is revealed on a load: which columns show is answered by the VALUES the exercise
    // carries across its cycles, which is what makes a column the trainer removed stay removed with
    // nothing stored to say so.
    revealedColumns: [],
    // Per-exercise this is always `false`; the DAY pass in `dayToDraft` derives the real answer,
    // because D12's veto needs the neighbours and no exercise can see them. Header note 4.
    showsRestColumn: false,
    sets,
    // READ-ONLY, and `null` rather than `[]` when the field is absent — the two are different
    // answers and the sheet's whole live variant hangs off which one arrived. See the field.
    executions: executionsFromWire(exercise.executions),
  };
}

/** Collapse a day's first session into the draft day. A day with more than one is refused upstream. */
export function dayToDraft(day: DayResponse, phaseStartCycle: number | null): DayDraft {
  const session = (day.sessions ?? [])[0];
  const exercises = (session?.exercises ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((exercise) => exerciseToDraft(exercise, phaseStartCycle));
  return {
    // Echoed back verbatim so a reorder, or a sibling's deletion, cannot make this day read as a
    // different one.
    slotToken: slotTokenOrMint(day.slotToken),
    // Captured, never emitted: it tells an existing day from a newly-added one for the
    // sequence-based recalc preview. A day absent from the loaded response is, by definition, new.
    serverId: day.id ?? null,
    // The position the server holds for this day — the baseline a later reorder is measured
    // against. It must be captured rather than derived: a reorder preserves `slotToken` and
    // `serverId`, so nothing else records where the day used to sit.
    serverOrder: day.order ?? null,
    name: day.name ?? null,
    sessionName: session?.name ?? null,
    // Read with no surface to render it. The save is whole-tree replace, so a field this mapper
    // skips is a field the trainer's next save DELETES — and this note is authored on the phone,
    // where the session-note editor already ships. Preservation does not wait for the editor.
    sessionNotes: session?.notes ?? null,
    dayOfWeek: day.dayOfWeek ?? null,
    restDaysAfter: day.restDaysAfter ?? null,
    // The day's authored cycle lifespan, echoed back AS AUTHORED — the response's own contract is
    // that an absent end stays absent, and this app's whole-tree save has to keep it that way. A
    // forgotten end widens the day over every remaining cycle of its phase and mints workouts the
    // trainer deleted; a forgotten exclusion list un-holes it.
    activationCycle: cycleCoordinate(day.activationCycle),
    retirementCycle: cycleCoordinate(day.retirementCycle),
    excludedCycles: day.excludedCycles ?? [],
    // Server-resolved and server-owned. Captured for the gestures that have to grey themselves; both
    // are excluded from the emit and from the conflict fingerprint.
    serverCycles: day.cycles ?? null,
    liveClosedCycles: day.liveClosedCycles ?? null,
    durationMinutes: session?.estimatedDurationMinutes ?? null,
    restSeconds: session?.defaultRestTimeSeconds ?? null,
    // D3 ∘ D12, applied once the whole day is known. It has to happen HERE rather than per exercise:
    // the veto is about a superset member's NEIGHBOURS, and the comparison is against the day's own
    // rest, neither of which an exercise can see on its own.
    exercises: deriveRestColumns(exercises, dayRestSeconds(session?.defaultRestTimeSeconds)),
  };
}

export function phaseToDraft(phase: PhaseResponse): PhaseDraft {
  // The phase's absolute first cycle — the base cycle of every exercise present since it began.
  // Null on a response predating the field, which `authoredSets` handles.
  const phaseStartCycle = cycleCoordinate(phase.startCycle);
  const days = (phase.days ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((day) => dayToDraft(day, phaseStartCycle));
  return {
    // Captured AND re-emitted on save: this is the phase's identity across the round trip, which
    // the backend needs to judge the consumed prefix — position, type and length cannot tell two
    // phases of the same shape apart. A phase added or duplicated in this session has none, and
    // correctly reads as new.
    serverId: phase.id ?? null,
    name: phase.name ?? '',
    type: phaseTypeFromApi(phase.phaseType),
    cycles: phase.cycleCount ?? 1,
    startCycle: phaseStartCycle ?? 1,
    days,
  };
}

/**
 * Snapshot the structural baseline of the tree just loaded.
 *
 * Taken from the MAPPED drafts rather than the raw response, so the baseline and the tree it will
 * later be compared against are read through the same normalisation — a `cycleCount ?? 1` default
 * that applied on the way in must apply to the baseline too, or an untouched phase would read as
 * resized.
 *
 * **Exported for the tests, and specifically so they stop rebuilding it.** Two suites used to
 * assemble this shape by hand to make a fork out of a locally-built tree, and both went stale the
 * moment the baseline grew a field — as a crash inside `pendingStructuralEdits`, not as a failed
 * assertion, because `Object.fromEntries` types its result too loosely for the compiler to notice.
 */
export function captureServerStructure(phases: readonly PhaseDraft[]): ServerPlanStructure {
  const captured: Record<number, ServerPhaseStructure> = {};
  for (const phase of phases) {
    if (phase.serverId == null) continue;
    const dayRestDays: Record<number, number | null> = {};
    const dayWeekdays: Record<number, number | null> = {};
    const dayWindows: Record<number, ServerDayWindow> = {};
    for (const day of phase.days) {
      if (day.serverId == null) continue;
      dayRestDays[day.serverId] = day.restDaysAfter;
      dayWeekdays[day.serverId] = day.dayOfWeek;
      // The AUTHORED window, copied rather than referenced: the draft's array is about to be handed
      // to Immer, and a baseline sharing it would follow every edit it is supposed to measure.
      dayWindows[day.serverId] = {
        activationCycle: day.activationCycle,
        retirementCycle: day.retirementCycle,
        excludedCycles: [...day.excludedCycles],
      };
    }
    captured[phase.serverId] = {
      cycles: phase.cycles,
      startCycle: phase.startCycle,
      dayRestDays,
      dayWeekdays,
      dayWindows,
    };
  }
  return { phases: captured };
}

/**
 * How many days in this plan carry more than one session — that is, how many this builder cannot
 * represent.
 *
 * A day may legitimately hold up to four sessions (training-plan R18: the day answers WHEN, the
 * session answers WHAT, and each becomes its own workout on that day's date). This builder authors
 * exactly one and has no interface for a second, so `dayToDraft` keeps `sessions[0]`.
 */
function multiSessionDayCount(response: FullResponse): number {
  return (response.phases ?? []).reduce(
    (total, phase) =>
      total + (phase.days ?? []).filter((day) => (day.sessions ?? []).length > 1).length,
    0,
  );
}

/**
 * Read one plan into an editable draft, or refuse it with a reason.
 *
 * **The refusal is plan-wide, and it is not caution for its own sake.** A save submits the WHOLE
 * tree (document-model ADR §2), so opening a plan whose extra sessions were dropped and then
 * editing anything at all — a name in another phase — would delete the client's scheduled workouts
 * for those sessions. Refusing only the offending day would not help: the same save still goes out.
 *
 * The web app inherits mobile's refusal (view ADR open question 4) because the alternative needs a
 * backend change this app has no standing to make: **sessions carry no slot token.** They are
 * matched by position within their day, so reordering, inserting or removing one of several
 * siblings can silently reassign which not-started workout a session's content applies to, with no
 * token for the client to detect the swap. Mobile's outright refusal is the one client-side
 * mitigation available, and it stays that until a session-level token exists.
 *
 * ── The SECOND refusal: a scheduling system this build has no canvas for ─────────────────────
 * Asked FIRST, because it is the more fundamental of the two and because the multi-session copy
 * sends the trainer to the phone, which is not advice worth giving about a system no client draws.
 *
 * **Two modes are drawable now**, `DateBased` on the weekday grid and `SelfPaced` on the tape, and
 * the refusal narrowed to genuinely unknown names rather than being deleted. The edge that makes it
 * necessary is sharper than the multi-session one: the save is whole-tree and re-emits
 * `PlanDraft.mode`, so a third mode opened on either canvas would be CONVERTED to that canvas's
 * mode by the trainer's first save. `schedulingModeFromApi` is the allow-list, and a blank is the
 * one tolerated value — see it for why.
 */
export function readPlanDocument(response: FullResponse): PlanDocumentRead {
  const planName = response.name?.trim() || null;

  if (schedulingModeFromApi(response.schedulingMode) === null) {
    return { outcome: 'refused', refusal: { reason: 'unsupportedSchedulingMode', planName } };
  }

  const dayCount = multiSessionDayCount(response);
  if (dayCount > 0) {
    return { outcome: 'refused', refusal: { reason: 'multiSessionDay', dayCount, planName } };
  }
  return { outcome: 'draft', draft: responseToDraft(response) };
}

/**
 * The mapping itself. Exported for the tests, which reach every decision through it; screens go
 * through `readPlanDocument`, which is the entry point that can refuse.
 */
export function responseToDraft(response: FullResponse): PlanDraft {
  // A client's fork reports `isLibraryEntryTemplate === false`. That is the authoritative live-plan
  // signal; it drives the chrome and the lock display, and never gates an edit — the backend owns
  // propagation and re-checks everything regardless.
  const isLibraryEntry = response.isLibraryEntryTemplate !== false;
  const isForkTemplate = !isLibraryEntry;

  const phases = (response.phases ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(phaseToDraft);

  const totalCycles = phases.reduce((sum, phase) => sum + Math.max(1, phase.cycles), 0);

  // The DISPLAY marker: the first cycle with a not-yet-started workout. Clamped into the loaded
  // span so a stale or ahead value cannot label a cycle the plan does not hold. Never an edit gate.
  const rawFirstOpen = response.liveFirstOpenCycle ?? null;
  const firstOpenCycle =
    isForkTemplate && rawFirstOpen != null
      ? Math.min(Math.max(1, rawFirstOpen), Math.max(1, totalCycles))
      : null;

  // The EDIT-GATE coordinate: the highest cycle the client has already consumed. `null` means there
  // is no live assignment behind this fork at all — nothing to protect, so nothing locked. `0`
  // means a live assignment with nothing trained yet, so the full length is still free. Clamped
  // into the loaded span so a stale value cannot freeze more than the plan actually holds.
  const rawLastConsumed = response.liveLastConsumedCycle ?? null;
  const lastConsumedCycle =
    isForkTemplate && rawLastConsumed != null
      ? Math.min(Math.max(0, rawLastConsumed), Math.max(0, totalCycles))
      : null;

  return {
    id: response.id ?? null,
    // TRIMMED at the seam. The name is also the cover's seed (`planCover`'s deterministic fold), and
    // the library row seeds from `textOrNull(item.name)` — which trims. Reading one side raw made a
    // plan stored with a trailing space fold to a different index in the builder than in the row it
    // was opened from, so the header and the list showed two different photographs of one plan.
    name: (response.name ?? '').trim(),
    description: response.description ?? null,
    category: response.category ?? null,
    coverId: response.coverId ?? null,
    // Display only. Derived by the backend from `coverId`, so it is never emitted — see the field.
    coverImageUrl: response.coverImageUrl ?? null,
    // Which canvas this plan opens on. The `?? 'date'` is unreachable through `readPlanDocument`,
    // which is the only way a screen gets here and which has already refused every mode with no
    // canvas — it is the fallback for a test or a caller reaching this mapper directly, and it
    // matches the blank-mode reading the allow-list already takes.
    mode: schedulingModeFromApi(response.schedulingMode) ?? 'date',
    isLibraryEntry,
    // A tree the server stored is assigned as what it is, so its lineage is the server's business.
    // `copyTemplateDraft` is the one producer that says otherwise.
    origin: AUTHORED_ORIGIN,
    phases,
    live: {
      isForkTemplate,
      // Merged in by the screen from the assignment it came through; the full read carries no
      // client name of its own.
      clientName: null,
      firstOpenCycle,
      lastConsumedCycle,
      totalCycles: totalCycles > 0 ? totalCycles : null,
      // The fork root's opaque concurrency token, echoed back on save verbatim.
      baseVersion: response.version ?? null,
      // Both are fork-only: a library entry has no assignment to deliver and no client schedule to
      // disturb.
      executionMode: isForkTemplate ? executionModeFromApi(response.liveExecutionMode) : null,
      serverStructure: isForkTemplate ? captureServerStructure(phases) : null,
      // `null` rather than `[]` when the wire says nothing, and the distinction is the whole value of
      // the field: `[]` means "no cycle is closed to a new day", `null` means "this surface did not
      // answer" — which the assignment-embedded template never does. A caller that collapsed the two
      // would offer an add-day affordance on a week the backend accepts and then never schedules.
      cyclesClosedToNewDays: response.liveCyclesClosedToNewDays ?? null,
    },
  };
}
